import { type ReactFlowInstance, useNodesData, useReactFlow } from '@xyflow/react';
import constate from 'constate';
import { useCallback, useMemo, useState } from 'react';
import { z } from 'zod';

import { isDefined, useQueryParams } from 'common';

import { getActiveRunningBlockNode } from './activeNode';
import { useFlowBuilderAssistantContext } from './assistant';
import { FlowUiMode } from './uiMode';
import { useFlowViewRelationships } from './useFlowViewRelationships';
import {
  type ReactFlowEdge,
  type ReactFlowNode,
  type ReactFlowPlaceholderNode,
  isBlockNode,
  isOutputBlockNode,
  isPartialBaseBlockNodeWithData,
  isPartialBlockNodeWithData,
  isPlaceholderBlockNode,
  isStartBlockNode,
} from '../../types';
import { type AddNodeProps, NodeConfigTab } from '../types';

export enum FlowDrawerState {
  Closed = 'Closed',
  SelectedNodeConfig = 'SelectedNodeConfig',
  FlowRunCost = 'FlowRunCost',
}

const FlowUiModesWithVisibleErrors = new Set([FlowUiMode.Edit, FlowUiMode.ReadOnly]);

const FlowUiModesWithVisibleMocks = new Set([FlowUiMode.Edit, FlowUiMode.Run]);

interface UseFlowViewProps {
  uiMode: FlowUiMode;
  nodes: ReactFlowNode[];
  edges: ReactFlowEdge[];
  reactFlowMutators?: {
    addPlaceholderNode: (newNodeProps: AddNodeProps) => ReactFlowPlaceholderNode;
    updateNode: ReactFlowInstance<ReactFlowNode, ReactFlowEdge>['updateNode'];
    updateNodeData: ReactFlowInstance<ReactFlowNode, ReactFlowEdge>['updateNodeData'];
    deleteNode: (nodeId: string) => void;
    deleteEdge: (edgeId: string) => void;
    deleteEdges: (edgeIds: string[]) => void;
  };
}

function useReactFlowView(props: UseFlowViewProps) {
  const { uiMode, nodes, edges, reactFlowMutators } = props;
  const isEditable = uiMode === FlowUiMode.Edit;
  const { fitView, getNode } = useReactFlow();
  const areBlockErrorsVisible = FlowUiModesWithVisibleErrors.has(uiMode);
  const blockNodes = useMemo(() => {
    return nodes.filter(isBlockNode);
  }, [nodes]);
  const activeRunningBlockNode = useMemo(() => {
    if (uiMode !== FlowUiMode.Run) {
      return undefined;
    }
    return getActiveRunningBlockNode(blockNodes);
  }, [uiMode, blockNodes]);
  const startBlockNode = blockNodes.find(isStartBlockNode);
  const outputBlockNode = blockNodes.find(isOutputBlockNode);

  const isMockEnabledVisible = FlowUiModesWithVisibleMocks.has(uiMode);
  const { nodesById, getAllParentNodeIds, getAllParentNodeNames, namesById, blockIdsByName } =
    useFlowViewRelationships({
      blockNodes,
      edges,
    });
  const basePlaceholderNode = nodes.find((node) => isPlaceholderBlockNode(node));

  const { queryParams, updateQueryParam } = useQueryParams({
    schema: z.object({
      selectedNodeId: z.string().optional(),
    }),
    initialValues: {
      selectedNodeId: basePlaceholderNode?.id,
    },
  });
  const { selectedNodeId } = queryParams;
  const [drawerState, setDrawerState] = useState(
    isDefined(selectedNodeId) ? FlowDrawerState.SelectedNodeConfig : FlowDrawerState.Closed
  );

  const [blockDrawerTab, setBlockDrawerTab] = useState<NodeConfigTab>(NodeConfigTab.Configuration);

  const setDrawerStateAndUpdateSelectedNodeId = useCallback(
    (newDrawerState: FlowDrawerState) => {
      if (newDrawerState !== FlowDrawerState.SelectedNodeConfig) {
        updateQueryParam('selectedNodeId', undefined);
      }

      setDrawerState(newDrawerState);
    },
    [setDrawerState, updateQueryParam]
  );

  const setSelectedNodeIdAndUpdateDrawerState = useCallback(
    (newNodeId: string | undefined) => {
      if (isDefined(newNodeId)) {
        setDrawerState(FlowDrawerState.SelectedNodeConfig);
      } else {
        setDrawerState(FlowDrawerState.Closed);
      }

      setBlockDrawerTab(NodeConfigTab.Configuration);

      updateQueryParam('selectedNodeId', newNodeId);
    },
    [updateQueryParam, setDrawerState, setBlockDrawerTab]
  );
  const selectedNodeWithData = useNodesData<ReactFlowNode>(selectedNodeId ?? '');
  const selectedNode = isPartialBlockNodeWithData(selectedNodeWithData)
    ? selectedNodeWithData
    : undefined;
  const selectedNodeData = selectedNode?.data;
  const selectedPlaceholderNode =
    isPartialBaseBlockNodeWithData(selectedNodeWithData) &&
    isPlaceholderBlockNode(selectedNodeWithData)
      ? selectedNodeWithData
      : undefined;

  const showNodeOnCanvas = useCallback(
    (nodeId: string) => {
      const node = getNode(nodeId);
      if (isDefined(node)) {
        fitView({
          nodes: [node],
          duration: 1000,
          maxZoom: 1,
        }).catch(console.error);
      }
    },
    [getNode, fitView]
  );

  const { isAssistantOpen, setIsAssistantOpen } = useFlowBuilderAssistantContext();

  return {
    nodes,
    blockNodes,
    blockIdsByName,
    activeRunningBlockNode,
    startBlockNode,
    outputBlockNode,
    edges,
    nodesById,
    namesById,
    getAllParentNodeIds,
    getAllParentNodeNames,
    uiMode,
    isEditable,
    areBlockErrorsVisible,
    isMockEnabledVisible,
    selectedNode,
    selectedNodeData,
    selectedPlaceholderNode,
    reactFlowMutators,
    drawerState,
    setDrawerState: setDrawerStateAndUpdateSelectedNodeId,
    selectedNodeId,
    setSelectedNodeId: setSelectedNodeIdAndUpdateDrawerState,
    showNodeOnCanvas,
    isAssistantOpen,
    setIsAssistantOpen,
    blockDrawerTab,
    setBlockDrawerTab,
  };
}

export const [ReactFlowViewProvider, useReactFlowViewContext] = constate(useReactFlowView);
